import React, { useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { FitbitMetric, FitbitStoredRecord } from '../../../services/fitbitCollectionDataService';
import { formatNumber, humanize, metricPresentation } from '../collection/metrics/metricPresentation';
import { metricModule } from '../collection/metrics/registry';
import FitbitRecordPreview, { recordTime } from './FitbitRecordPreview';
import { intervalMinutes, measurementObject, structuredChartKinds } from './structuredMeasurements';
import FitbitCategoryChart from './FitbitCategoryChart';
import FitbitZoneLimitsChart from './FitbitZoneLimitsChart';

function RecordedPeriods({ records, dataType }: { records: FitbitStoredRecord[]; dataType: string }) {
  const present = records.filter(record => record.valueState === 'present');
  const [page, setPage] = useState(0);
  if (!present.length) return <Alert severity="info">No periods were recorded in these dates.</Alert>;
  return <>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Each row is a recorded period. Duration is shown only when both start and end timestamps are available.</Typography>
    <TableContainer><Table size="small" aria-label="Recorded periods"><TableHead><TableRow>
      <TableCell>Recorded period</TableCell><TableCell>Category</TableCell><TableCell>Duration</TableCell>
    </TableRow></TableHead><TableBody>{present.slice(page * 20, (page + 1) * 20).map((record, i) => {
      const value = measurementObject(record.measurement);
      const duration = intervalMinutes(record);
      const category = value.activityLevelType || value.heartRateZoneType;
      return <TableRow key={i}><TableCell sx={{ overflowWrap: 'anywhere' }}>{recordTime(record)}</TableCell>
        <TableCell><Chip size="small" variant="outlined" label={typeof category === 'string' ? humanize(category) : dataType === 'sedentary-period' ? 'Sedentary' : dataType === 'irregular-rhythm-notification' ? 'Notification' : 'Unspecified'} /></TableCell>
        <TableCell>{duration === null ? 'Not provided' : `${formatNumber(duration)} min`}</TableCell>
      </TableRow>;
    })}</TableBody></Table></TableContainer>
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
      <Button disabled={!page} onClick={() => setPage(page - 1)}>Previous periods</Button>
      <Typography variant="body2">Page {page + 1} of {Math.ceil(present.length / 20)}</Typography>
      <Button disabled={(page + 1) * 20 >= present.length} onClick={() => setPage(page + 1)}>Next periods</Button>
    </Stack>
  </>;
}

export default function FitbitStructuredChart({ metric, records, hasMore, dates }: {
  metric: FitbitMetric; records: FitbitStoredRecord[]; hasMore: boolean; dates: string[];
}) {
  const [details, setDetails] = useState(false);
  const presentation = metricPresentation(metric);
  // These chart kinds describe Fitbit record shapes. Another device's metric
  // that shares a data type name must not be drawn with them.
  const kind = metricModule(metric).id === 'fitbit' ? structuredChartKinds[metric.dataType] : undefined;
  const periods = kind === 'duration' || kind === 'notifications';
  if (!kind) return <FitbitRecordPreview records={records} hasMore={hasMore} />;
  return <>
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
      <Typography variant="h6" component="h2">{presentation.label}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: .5, mb: 2.5 }}>{presentation.description}</Typography>
      {hasMore && <Alert severity="warning" sx={{ mb: 2 }}>This preview uses the first {records.length.toLocaleString()} records, so it does not cover all stored data in these dates. Narrow the date range for a complete chart, or export all records.</Alert>}
      {kind === 'limits' ? <FitbitZoneLimitsChart records={records} dates={dates} /> :
        <FitbitCategoryChart records={records} dates={dates} partial={hasMore} dataType={metric.dataType} label={presentation.label} />}
      <Button sx={{ mt: 2 }} onClick={() => setDetails(!details)} aria-expanded={details}>{details ? 'Hide' : 'View'} {periods ? 'recorded periods' : 'record details'}</Button>
      {details && periods && <Box sx={{ mt: 2 }}><RecordedPeriods records={records} dataType={metric.dataType} /></Box>}
    </Paper>
    {details && !periods && <Box sx={{ mt: 2 }}><FitbitRecordPreview records={records} hasMore={hasMore} /></Box>}
  </>;
}
